import React, { useState, useEffect, useRef } from 'react';
import { AgentNotificationSettings } from './NotificationPreferences';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { useToast } from './Toast';
import { createCheckoutSession, createPortalSession, updateSubscription, updateHomeownerAddon, STRIPE_PRICES, TEAM_PRICING } from '../utils/stripe';
import { subscriptionAllowsAccess } from '../utils/subscriptionAccess';
import { showsTeamScreen } from '../shared/teamSeats';
import { agentAuthHeaders } from '../utils/functionAuth';
import { getApiUrl, isNativeApp, isWebApp, NATIVE_PLAN_NOTICE } from '../utils/platform';
import { shareTextFile, shareWasCancelled } from '../utils/nativeShare';
import AgentAccountDelete from './AgentAccountDelete';
import AccountSignIn from './AccountSignIn';
import { AgentPushSetting } from './AccountPushSwitch';
import SupportAccessSettings from './SupportAccessSettings';
import { FIRST_PAID_REFUND_NOTICE, LEGAL_INDEX_PATH, followPolicyLink } from '../utils/legal';
import { useTheme } from '../ThemeContext';
import { secondaryButtonStyle } from '../shared/secondaryButton';
import { formatChargeAmount, monthlyPlanPrice, profileHasSubscription, trialHasPaymentMethod, trialStatusCopy } from '../shared/trialStatus';
import { homeownerLimit } from '../shared/homeownerIntake';
import { addonConfirmMessage as addonConfirmCopy, addonQuote, changePlanAddonLines, changePlanAddonNote, formatHomeownerCount, homeownerPoolRow, homeownerUsageLabel, homeownerUsageWarning, normalizeAddon, purchasedSeats, seatMonthlyTotal } from '../shared/planLimits';
import HomeownerAddonCard from './HomeownerAddonCard';
import HomeownerGraceBanner from './HomeownerGraceBanner';
import { AgentPastDueNotice } from './AgentPastDueNotice';
import { teamBillingCopy } from '../shared/teamBilling';
import { checkoutPlanLabel } from '../shared/planNames';
import { checkoutTrialDays, seatChangeAllowed, seatFloorMessage } from '../shared/planSwitchUi';
import TeamBillingCard from './TeamBillingCard';
import ConfirmDialog from './ConfirmDialog';
import AppearanceChoice from './AppearanceChoice';
import TrialConsentNotice, { SubscribeConsentNotice, subscribeButtonFor, trialButtonFor } from './TrialConsentNotice';
import { addonKeptOnSubscriptionChange } from '../shared/consentCopy';
import { displayedRefundVariant, trialConsentApplies } from '../shared/consentCopy';

function menuRowStyle(colors) {
  return {
    width: '100%',
    padding: '1rem 1.25rem',
    border: 'none',
    borderBottom: `1px solid ${colors.border}`,
    background: 'transparent',
    color: colors.text,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    fontSize: '1rem',
    fontWeight: 500,
    fontFamily: 'inherit',
    minHeight: '56px',
    textAlign: 'left',
    boxSizing: 'border-box',
  };
}

function TrialNoticeText({ notice, colors, onManageBilling }) {
  const detail = (notice && notice.detail) || '';
  const marker = ' online here.';
  const splitAt = notice && notice.hasCard ? detail.indexOf(marker) : -1;
  const textStyle = { fontSize: '0.95rem', color: colors.text, margin: 0, lineHeight: 1.45 };
  if (splitAt === -1) {
    return <p style={textStyle}>{detail}</p>;
  }
  const before = detail.slice(0, splitAt + ' online '.length);
  const after = detail.slice(splitAt + ' online here'.length);
  return (
    <p style={textStyle}>
      {before}
      <button
        type="button"
        onClick={onManageBilling}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: '#59c4c4',
          font: 'inherit',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        here
      </button>
      {after}
    </p>
  );
}

const Settings = ({ profile, onLogout, onNavigate, onReplayTour, changePlanRequest, changePlanPreset, moreListToken }) => {
  const { colors, isDark } = useTheme();
  const { showToast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openSection, setOpenSection] = useState(() => {
    if (typeof window === 'undefined') return null;
    if (window.location.hash === '#billing') return 'billing';
    if (window.location.hash === '#account') return 'account';
    return null;
  });
  const moreListSeen = useRef(moreListToken);
  useEffect(() => {
    if (moreListSeen.current === moreListToken) return;
    moreListSeen.current = moreListToken;
    setOpenSection(null);
  }, [moreListToken]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [joinTeamCode, setJoinTeamCode] = useState('');
  const [joiningTeam, setJoiningTeam] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [planConfirmMessage, setPlanConfirmMessage] = useState('');
  const [trialPlanConsent, setTrialPlanConsent] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [addonConfirmMessage, setAddonConfirmMessage] = useState('');
  const [changingAddon, setChangingAddon] = useState(false);
  const [team, setTeam] = useState(null);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [poolUsage, setPoolUsage] = useState(null);
  const [billingRemote, setBillingRemote] = useState(null);

  useEffect(() => {
    if (!profile || !profile.stripe_customer_id || profile.stripe_subscription_id || isNativeApp()) {
      setBillingRemote(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(getApiUrl('/.netlify/functions/billing-customer'), {
          headers: await agentAuthHeaders(),
        });
        const body = await response.json().catch(() => ({}));
        if (cancelled) return;
        setBillingRemote(response.ok && body ? body : {
          hasSubscription: false,
          hasDefaultPaymentMethod: false,
          trialPreviouslyUsed: false,
          refundVariant: 'prior_charge',
        });
      } catch (error) {
        if (!cancelled) {
          setBillingRemote({
            hasSubscription: false,
            hasDefaultPaymentMethod: false,
            trialPreviouslyUsed: false,
            refundVariant: 'prior_charge',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile]);

  useEffect(() => {
    if (!profile?.team_id) {
      setTeam(null);
      setPendingInvites(0);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const teamResult = await supabase
        .from('teams')
        .select('id, name, max_seats, current_seats, homeowner_addon')
        .eq('id', profile.team_id)
        .maybeSingle();
      if (cancelled) return;
      const data = teamResult && teamResult.data;
      setTeam(data || null);
      if (!data) {
        setPendingInvites(0);
        return;
      }
      try {
        const inviteResult = await supabase
          .from('team_invites')
          .select('id', { count: 'exact', head: true })
          .eq('team_id', profile.team_id)
          .eq('status', 'pending');
        if (!cancelled) setPendingInvites(Number(inviteResult && inviteResult.count) || 0);
      } catch (error) {
        if (!cancelled) setPendingInvites(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile?.team_id]);

  useEffect(() => {
    if (!profile?.id || typeof supabase.rpc !== 'function') {
      setPoolUsage(null);
      return undefined;
    }
    let cancelled = false;
    supabase.rpc('homeowner_pool_usage').then((result) => {
      if (cancelled || !result || result.error) return;
      const row = homeownerPoolRow(result.data);
      if (row) setPoolUsage(row);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [profile?.id, profile?.team_id]);

  const handleExportData = async () => {
    try {
      // Fetch all data
      const [clientsRes, vendorsRes, tasksRes] = await Promise.all([
        supabase.from('clients').select('*').eq('agent_id', profile.id),
        supabase.from('vendors').select('*').eq('agent_id', profile.id),
        supabase.from('tasks').select('*').eq('agent_id', profile.id)
      ]);

      const data = {
        profile: profile,
        clients: clientsRes.data || [],
        vendors: vendorsRes.data || [],
        tasks: tasksRes.data || [],
        exported_at: new Date().toISOString()
      };

      const dataStr = JSON.stringify(data, null, 2);
      const filename = `pacarion-data-${new Date().toISOString().split('T')[0]}.json`;
      const where = await shareTextFile(filename, dataStr, 'application/json');
      showToast(
        where === 'shared'
          ? `Choose where to save ${filename}.`
          : 'Your file is in Downloads. On iPhone, look in the Files app.',
        'success'
      );
    } catch (error) {
      if (shareWasCancelled(error)) return;
      console.error('Error exporting data:', error);
      showToast('Error exporting data. Please try again.');
    }
  };

  const hasSubscription = profileHasSubscription(profile, billingRemote);
  const awaitingTrialCheck = Boolean(
    profile && profile.stripe_customer_id && !profile.stripe_subscription_id && !billingRemote
  );
  const showNewTrialLine = checkoutTrialDays(profile) === 14
    && !awaitingTrialCheck
    && !(billingRemote && billingRemote.trialPreviouslyUsed);
  const chargedToday = !hasSubscription && !showNewTrialLine && !awaitingTrialCheck;
  const subscribeRefundVariant = displayedRefundVariant(profile, billingRemote);

  const startCheckout = async (priceId, consent) => {
    if (isNativeApp()) return;
    if (!priceId) {
      showToast('Checkout is not configured for that plan.');
      return;
    }
    await createCheckoutSession({
      priceId,
      agentId: profile.id,
      email: profile.email,
      consent,
    });
  };

  const planNeedsTrialConsent = (plan) => {
    if (!plan || !profile) return false;
    if (!hasSubscription) return showNewTrialLine;
    return trialConsentApplies({ profile, checkout: false });
  };

  const planNeedsSubscribeConsent = (plan) => Boolean(plan && chargedToday);

  const handleManageBilling = async () => {
    if (isNativeApp()) return;
    if (!hasSubscription) {
      setShowChangePlanModal(true);
      const stillTrial = showNewTrialLine || (checkoutTrialDays(profile) === 14 && awaitingTrialCheck);
      showToast(stillTrial
        ? 'Choose a plan and agree to the free trial before checkout.'
        : 'Choose a plan. Your card will be charged today.');
      return;
    }

    try {
      await createPortalSession(profile.stripe_customer_id);
    } catch (error) {
      console.error('Error opening billing portal:', error);
      showToast(`Unable to open billing portal: ${error.message}\n\nPlease try again or contact support.`);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openPortal = params.get('billing') === 'portal';
    const hash = window.location.hash;
    const jumpToBilling = hash === '#billing' || hash === '#billing-plus' || hash === '#billing-max';
    if (jumpToBilling) {
      setOpenSection('billing');
      if (hash === '#billing-plus') setSelectedAddon('plus');
      if (hash === '#billing-max') setSelectedAddon('max');
      const node = document.getElementById('billing');
      if (node) node.scrollIntoView({ block: 'start' });
    }
    if (openPortal || jumpToBilling) {
      params.delete('billing');
      const next = params.toString();
      window.history.replaceState({}, document.title, `${window.location.pathname}${next ? `?${next}` : ''}`);
    }
    if (openPortal && !isNativeApp()) handleManageBilling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoinTeam = async () => {
    if (!joinTeamCode.trim()) {
      showToast('Please enter an invite code');
      return;
    }

    if (profile.team_id) {
      showToast('You are already part of a team. Please leave your current team first or contact support.');
      return;
    }

    setJoiningTeam(true);

    try {
      const trimmedCode = joinTeamCode.trim().toUpperCase();

      // Validate invite code
      const { data: inviteData, error: inviteError } = await supabase
        .from('team_invites')
        .select('*, teams!inner(id, name, owner_id, max_seats, current_seats)')
        .eq('invite_code', trimmedCode)
        .eq('status', 'pending')
        .single();

      if (inviteError || !inviteData) {
        showToast('Invalid or expired invite code. Please check the code and try again.');
        setJoiningTeam(false);
        return;
      }

      // Check if invite has expired
      const expirationDate = new Date(inviteData.expires_at);
      if (expirationDate < new Date()) {
        showToast('This invite code has expired. Please contact the team owner for a new code.');
        setJoiningTeam(false);
        return;
      }

      // Check if team has available seats
      if (inviteData.teams.current_seats >= inviteData.teams.max_seats) {
        showToast(`This team is full (${inviteData.teams.max_seats}/${inviteData.teams.max_seats} seats used). Please contact the team owner.`);
        setJoiningTeam(false);
        return;
      }

      // Cancel existing Solo subscription if they have one
      if (profile.stripe_customer_id && profile.subscription_plan === 'solo') {
        const confirmed = window.confirm(
          `You currently have an active Solo subscription ($49/month).\n\n` +
          `Joining ${inviteData.teams.name} will:\n` +
          `• Cancel your Solo subscription (no prorated refund; ${FIRST_PAID_REFUND_NOTICE})\n` +
          `• Transfer you to the team (no charge to you)\n` +
          `• Team owner will be billed for your seat\n\n` +
          `Continue?`
        );

        if (!confirmed) {
          setJoiningTeam(false);
          return;
        }
      }

      const response = await fetch(getApiUrl('/.netlify/functions/profile-entitlements'), {
        method: 'POST',
        headers: await agentAuthHeaders(),
        body: JSON.stringify({ action: 'join-team', inviteCode: trimmedCode }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Update failed');
      }

      showToast(`Successfully joined ${inviteData.teams.name}!\n\nYou now have access to the team's shared resources. Please reload the page to see your team dashboard.`);

      setJoinTeamCode('');
      window.location.reload();
    } catch (error) {
      console.error('Error joining team:', error);
      showToast(error.message || 'Error joining team. Please try again or contact support.');
    } finally {
      setJoiningTeam(false);
    }
  };

  const planChangeMessage = (plan) => {
    const needsCheckout = !profileHasSubscription(profile, billingRemote);
    const planLabel = `${checkoutPlanLabel(plan.type, plan.agents)} ($${plan.price}/month)`;
    if (needsCheckout) {
      const lead = `Continue to checkout for ${planLabel}?`;
      if (chargedToday) return lead;
      return `${lead}\n\nYou will add a payment method there.`;
    }
    if (plan.type === 'solo') {
      return `Change to ${planLabel}?\n\nYou'll be able to manage up to ${plan.maxClients} homeowners.\n\nYour billing will be prorated automatically.`;
    }
    return `Change to ${planLabel}?\n\nYour team can manage up to ${plan.maxClients} homeowners total.\n\nYour billing will be prorated automatically.`;
  };

  const requestPlanChange = () => {
    if (!selectedNewPlan) {
      showToast('Please select a plan');
      return;
    }
    setTrialPlanConsent(false);
    setPlanConfirmMessage(planChangeMessage(selectedNewPlan));
  };

  const addonSeats = team && Number(team.max_seats) > 0 ? Number(team.max_seats) : 1;
  const addonShared = Boolean(profile && profile.team_id);
  const currentAddon = addonShared
    ? normalizeAddon(team && team.homeowner_addon)
    : normalizeAddon(profile && profile.homeowner_addon);
  const planChangeAddon = (plan) => {
    if (!plan) return 'none';
    if (planNeedsSubscribeConsent(plan)) return currentAddon;
    if (hasSubscription && planNeedsTrialConsent(plan)) {
      return addonKeptOnSubscriptionChange(plan.type, currentAddon);
    }
    return 'none';
  };

  const requestAddonChange = () => {
    if (!selectedAddon || selectedAddon === currentAddon) {
      showToast('Pick a different homeowner add-on');
      return;
    }
    setAddonConfirmMessage(addonConfirmCopy({
      addon: selectedAddon,
      seats: addonSeats,
      shared: addonShared,
    }));
  };

  const handleChangeAddon = async () => {
    if (!selectedAddon) return;
    setChangingAddon(true);
    try {
      await updateHomeownerAddon({ addon: selectedAddon });
      showToast('Homeowner add-on updated. Your billing was prorated.');
      setAddonConfirmMessage('');
      setSelectedAddon(null);
      window.location.reload();
    } catch (error) {
      console.error('Error changing homeowner add-on:', error);
      showToast(error.message || 'Could not update the homeowner add-on.');
      setChangingAddon(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedNewPlan) {
      showToast('Please select a plan');
      return;
    }
    if (
      selectedNewPlan.type === 'solo'
      && profile
      && profile.subscription_plan === 'team'
      && profile.team_id
    ) {
      showToast('Switch to Solo from the Team page, under Danger zone.');
      return;
    }

    const needsCheckout = !hasSubscription;
    if (needsCheckout && awaitingTrialCheck) {
      showToast('Checking your billing history. Try again in a moment.');
      return;
    }
    const needsTrialAgreement = planNeedsTrialConsent(selectedNewPlan);
    const needsSubscribeAgreement = planNeedsSubscribeConsent(selectedNewPlan);
    if ((needsTrialAgreement || needsSubscribeAgreement) && !trialPlanConsent) {
      showToast(needsSubscribeAgreement
        ? 'Check the box to agree to the monthly charge before continuing.'
        : 'Check the box to agree to the free trial and automatic monthly charge before continuing.');
      return;
    }
    const consent = needsSubscribeAgreement
      ? { autoRenewal: true, kind: 'subscribe', refundVariant: subscribeRefundVariant }
      : (needsTrialAgreement ? { autoRenewal: true, kind: 'trial' } : null);
    setPlanConfirmMessage('');
    setChangingPlan(true);

    try {
      if (needsCheckout) {
        await startCheckout(selectedNewPlan.priceId, consent);
        return;
      }
      try {
        await updateSubscription({
          customerId: profile.stripe_customer_id,
          newPriceId: selectedNewPlan.priceId,
          agentId: profile.id,
          consent,
        });
      } catch (updateError) {
        const missing = /no active or trial subscription/i.test(String((updateError && updateError.message) || ''));
        if (!missing) throw updateError;
        await startCheckout(selectedNewPlan.priceId, consent);
        return;
      }

      showToast('Plan updated successfully! Your changes will take effect immediately.');
      setShowChangePlanModal(false);
      setSelectedNewPlan(null);

      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Error changing plan:', error);
      showToast(`Error updating plan: ${error.message}\n\nPlease try again or contact support.`);
    } finally {
      setChangingPlan(false);
    }
  };

  // Get current plan details
  const getCurrentPlan = () => {
    const trialing = profile && profile.subscription_status === 'trialing';
    if (!profile || !subscriptionAllowsAccess(profile)) return null;
    if (!profile.stripe_customer_id && !trialing) return null;

    if (profile.subscription_plan === 'solo') {
      return {
        name: checkoutPlanLabel('solo', 1),
        price: seatMonthlyTotal(49, 1, currentAddon),
        maxClients: homeownerLimit(profile)
      };
    }

    if (profile.subscription_plan === 'team') {
      const maxClients = homeownerLimit(profile, team);
      const agents = purchasedSeats(team);
      const base = monthlyPlanPrice(profile, TEAM_PRICING, agents);

      return {
        name: checkoutPlanLabel('team', agents),
        price: base == null ? null : seatMonthlyTotal(base, agents, currentAddon),
        maxClients: maxClients
      };
    }

    return null;
  };

  const billing = teamBillingCopy(profile, team);
  const onTeamPlan = Boolean(
    profile && profile.subscription_plan === 'team' && (profile.team_id || profile.is_team_owner)
  );
  const rawSeatNote = seatFloorMessage(team, pendingInvites);
  const seatLimitNote = rawSeatNote.includes('danger zone') ? '' : rawSeatNote;
  const planBlocked = (agents) => Boolean(team) && !seatChangeAllowed({
    nextAgents: agents,
    currentSeats: team.current_seats,
    pendingInvites,
  }).ok;

  const nativeApp = isNativeApp();
  const currentPlan = getCurrentPlan();
  const trialNotice = profile && profile.subscription_status === 'trialing'
    ? trialStatusCopy({
      status: 'trialing',
      trialEndsAt: profile.trial_ends_at,
      price: currentPlan && currentPlan.price,
      planName: currentPlan
        ? currentPlan.name
        : checkoutPlanLabel(profile.subscription_plan === 'team' ? 'team' : 'solo', 1),
      hasCard: trialHasPaymentMethod(profile, billingRemote),
      omitCharge: nativeApp,
    })
    : null;

  const openTeamPlan = (teamOption) => {
    const option = teamOption || TEAM_PRICING[0];
    const quote = addonQuote({ addon: currentAddon, seats: option.agents, shared: true });
    const price = seatMonthlyTotal(option.price, option.agents, currentAddon);
    setSelectedNewPlan({
      type: 'team',
      agents: option.agents,
      price,
      maxClients: quote.pool,
      priceId: option.priceId,
    });
    setShowChangePlanModal(true);
  };

  useEffect(() => {
    if (!changePlanRequest || nativeApp) return;
    if (changePlanPreset === 'team') openTeamPlan(TEAM_PRICING[0]);
    else setShowChangePlanModal(true);
    setOpenSection('billing');
  }, [changePlanRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <HomeownerGraceBanner
        profile={profile}
        used={poolUsage ? poolUsage.used : null}
        colors={colors}
        onBuyPlus={() => {
          setOpenSection('billing');
          setSelectedAddon('plus');
        }}
        onBuyMax={() => {
          setOpenSection('billing');
          setSelectedAddon('max');
        }}
        onArchive={() => {
          if (onNavigate) onNavigate('clients');
        }}
      />
      {/* Mobile: More Menu - Show links to other sections */}
          {isMobile && onNavigate && !openSection && (
            <div style={{marginBottom: '1.5rem'}}>
              <h2 style={{fontSize: '1.5rem', margin: '0 0 0.75rem', fontWeight: 300, letterSpacing: '0.05em', color: colors.text}}>More</h2>
              <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, padding: '0', marginBottom: 0}}>
                {[
                  { id: 'clients', label: 'Clients' },
                  { id: 'vendors', label: 'Vendors' },
                  ...(showsTeamScreen(profile) ? [{ id: 'team', label: 'Team' }] : []),
                  ...(isWebApp() ? [{ id: 'integrations', label: 'Integrations' }] : []),
                  { id: 'home-history', label: 'Pacarion Home History Report' },
                ].map((item, index, array) => (
                  <button
                    key={item.id}
                    className="app-menu-row"
                    data-tour={item.id}
                    onClick={() => onNavigate(item.id)}
                    style={{
                      ...menuRowStyle(colors),
                      borderBottom: index < array.length - 1 ? `1px solid ${colors.border}` : 'none',
                    }}
                  >
                    <span style={{flex: 1, textAlign: 'left'}}>{item.label}</span>
                    <span style={{color: colors.textSecondary}}>›</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!openSection && (
            <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, padding: 0, marginBottom: '1.5rem'}}>
              {[
                { id: 'profile', label: 'Profile & Branding' },
                ...(isMobile && showsTeamScreen(profile) ? [] : [{ id: 'team', label: 'Team' }]),
                { id: 'billing', label: 'Billing' },
                { id: 'account', label: 'Account' },
                { id: 'data', label: 'Data & Privacy' },
                { id: 'support-access', label: 'Support access' },
              ].map((item, index, array) => (
                <button
                  key={item.id}
                  type="button"
                  className="app-menu-row"
                  onClick={() => setOpenSection(item.id)}
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    border: 'none',
                    background: 'transparent',
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '1rem',
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    borderBottom: index < array.length - 1 ? `1px solid ${colors.border}` : 'none',
                    minHeight: '56px',
                    textAlign: 'left',
                  }}
                >
                  <span>{item.label}</span>
                  <span style={{ color: colors.textSecondary }}>›</span>
                </button>
              ))}
              {onReplayTour && (
                <button
                  type="button"
                  className="app-menu-row"
                  data-tour="settings"
                  onClick={onReplayTour}
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    border: 'none',
                    borderTop: `1px solid ${colors.border}`,
                    background: 'transparent',
                    color: colors.text,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '1rem',
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    minHeight: '56px',
                  }}
                >
                  Replay tour
                </button>
              )}
            </div>
          )}

          {openSection && (
            <button
              type="button"
              onClick={() => setOpenSection(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.primary || '#59c4c4',
                fontSize: '1rem',
                fontWeight: 650,
                padding: '0 0 1rem',
                cursor: 'pointer',
                minHeight: '44px',
              }}
            >
              ‹ Settings
            </button>
          )}

          {/* Account Settings */}
          {openSection === 'notifications' && (
            <AgentNotificationSettings profile={profile} />
          )}

          {(openSection === 'team' || openSection === 'billing' || openSection === 'profile') && (
          <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>

            {/* Team Settings */}
            {openSection === 'team' && (
            <div style={{marginBottom: '2rem'}}>
              <h4 style={{...styles.subsectionTitle, color: colors.text}}>Team Settings</h4>
              <div style={styles.settingsContent}>
                {(profile.is_team_owner || profile.role === 'owner') && (
                  <div style={{marginBottom: '1rem'}}>
                    <p style={{marginTop: 0, color: colors.textSecondary}}>
                      Add agents by email from the team screen. Invite codes still work if someone already has one.
                    </p>
                    <button
                      type="button"
                      style={styles.btnPrimary}
                      onClick={() => onNavigate && onNavigate('team')}
                    >
                      Add team member
                    </button>
                  </div>
                )}
                {profile.team_id && !profile.is_team_owner && profile.role !== 'owner' ? (
                  <div style={{
                    background: colors.surfaceAlt,
                    border: `2px solid ${colors.primary}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{margin: 0, marginBottom: '0.5rem', color: colors.primary}}>
                      You're part of a team!
                    </h4>
                    <p style={{margin: 0, color: colors.textSecondary}}>
                      You are currently a member of a team. Your team owner manages the subscription and billing.
                    </p>
                    {profile.role === 'member' && (
                      <p style={{margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: colors.textSecondary}}>
                        To leave your team or manage team settings, please contact your team owner or support.
                      </p>
                    )}
                  </div>
                ) : !profile.team_id ? (
                  <>
                    <p style={{marginBottom: '1rem', color: colors.textSecondary}}>
                      Have an invite code? Enter it here to join a team. This is optional if you were invited by email.
                    </p>
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: colors.text}}>
                        Team Invite Code
                      </label>
                      <input
                        type="text"
                        value={joinTeamCode}
                        onChange={(e) => setJoinTeamCode(e.target.value.toUpperCase())}
                        placeholder="Enter 8-character code"
                        maxLength={8}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          fontSize: '1rem',
                          border: `2px solid ${colors.border}`,
                          background: colors.surfaceAlt,
                          color: colors.text,
                          borderRadius: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <button
                        style={styles.btnPrimary}
                        onClick={handleJoinTeam}
                        disabled={joiningTeam || !joinTeamCode.trim()}
                      >
                        {joiningTeam ? 'Joining Team...' : 'Join Team'}
                      </button>
                      <button
                        type="button"
                        style={styles.btnSecondary}
                        onClick={() => openTeamPlan(TEAM_PRICING[0])}
                      >
                        Start a team
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            )}

            {/* Billing */}
            {openSection === 'billing' && (
            <div id="billing" style={{marginBottom: '2rem', scrollMarginTop: '5rem'}}>
              <h4 style={{...styles.subsectionTitle, color: colors.text}}>Subscription & Billing</h4>
              <AgentPastDueNotice
                profile={profile}
                colors={colors}
                isDark={isDark}
                onUpdateCard={handleManageBilling}
              />
              <div style={styles.settingsContent}>
                {billing.audience !== 'solo' && (
                  <TeamBillingCard
                    copy={billing}
                    colors={colors}
                    isDark={isDark}
                    onManageBilling={handleManageBilling}
                    note={poolUsage && poolUsage.shared
                      ? homeownerUsageLabel(poolUsage.used, poolUsage.limit, true)
                      : ''}
                  />
                )}
                {/* Current Plan Display */}
                {billing.showPlan && !currentPlan && !trialNotice && !nativeApp && (
                  <p style={{marginBottom: '1rem', color: colors.textSecondary}}>
                    No subscription yet. Manage Billing and Change Plan open checkout.
                  </p>
                )}
                {billing.showPlan && currentPlan && (
                  <div style={{
                    background: colors.surfaceAlt || 'rgba(89, 196, 196, 0.1)',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                      <span style={{fontWeight: 'bold', color: colors.text}}>{currentPlan.name}</span>
                      {!nativeApp && (
                      <span style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#59c4c4'}}>
                        {formatChargeAmount(currentPlan.price)}/month
                      </span>
                      )}
                    </div>
                    <p style={{fontSize: '0.9rem', color: colors.textSecondary, margin: 0}}>
                      {poolUsage
                        ? homeownerUsageLabel(poolUsage.used, poolUsage.limit, poolUsage.shared)
                        : `Up to ${formatHomeownerCount(currentPlan.maxClients)} homeowners`}
                    </p>
                  </div>
                )}

                {poolUsage && homeownerUsageWarning(poolUsage.used, poolUsage.limit, poolUsage.shared) ? (
                  <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
                    {homeownerUsageWarning(poolUsage.used, poolUsage.limit, poolUsage.shared)}
                  </p>
                ) : null}

                {!nativeApp && billing.showChangePlan && hasSubscription && subscriptionAllowsAccess(profile) && (
                  <div>
                    <HomeownerAddonCard
                      currentAddon={currentAddon}
                      seats={addonSeats}
                      shared={addonShared}
                      selected={selectedAddon}
                      disabled={changingAddon}
                      colors={colors}
                      onSelect={setSelectedAddon}
                    />
                    <button
                      type="button"
                      style={{ ...styles.btnPrimary, marginTop: '0.75rem' }}
                      onClick={requestAddonChange}
                      disabled={!selectedAddon || selectedAddon === currentAddon || changingAddon}
                    >
                      {changingAddon ? 'Updating...' : 'Save add-on'}
                    </button>
                  </div>
                )}

                {billing.audience !== 'member' && trialNotice && (
                  <div style={{marginBottom: '1rem'}}>
                    <TrialNoticeText notice={trialNotice} colors={colors} onManageBilling={handleManageBilling} />
                  </div>
                )}

                {!nativeApp && billing.showChangePlan && profile?.stripe_customer_id ? (
                  <p style={{marginBottom: '1rem', color: colors.textSecondary}}>
                    Change your plan, update payment methods, and view invoices. Cancel anytime from Manage Billing.{' '}
                    <a href="/refunds" onClick={followPolicyLink} style={{ color: '#59c4c4' }}>Refund policy</a>
                  </p>
                ) : null}

                {!nativeApp && (billing.showChangePlan || (billing.showManageBilling && billing.audience === 'solo')) && (
                <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap'}}>
                  {billing.showChangePlan && (
                  <button
                    style={{
                      ...styles.btnPrimary,
                      background: '#59c4c4',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onClick={() => setShowChangePlanModal(true)}
                  >
                    Change Plan
                  </button>
                  )}
                  {billing.showManageBilling && billing.audience === 'solo' && (
                  <button
                    style={{
                      ...styles.btnSecondary,
                      ...secondaryButtonStyle(isDark),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onClick={handleManageBilling}
                  >
                    Manage Billing
                  </button>
                  )}
                </div>
                )}
                {nativeApp && (
                  <p style={{margin: '0.75rem 0 0', color: colors.text}}>{NATIVE_PLAN_NOTICE}</p>
                )}
              </div>
            </div>
            )}

            {/* Profile */}
            {openSection === 'profile' && (
            <div style={{marginBottom: '0'}}>
              <h4 style={{...styles.subsectionTitle, color: colors.text}}>Profile & Branding</h4>
              <div style={styles.settingsContent}>
                <p style={{marginBottom: '1rem', color: colors.textSecondary}}>
                  View and edit your profile information, branding, and contact details.
                </p>
                <button
                  style={{
                    ...styles.btnPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={() => onNavigate('profile')}
                >
                  Edit Profile
                </button>
              </div>
            </div>
            )}
          </div>
          )}

          {/* Data & Privacy */}
          {openSection === 'data' && (
          <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <h3 style={{...styles.sectionTitle, color: colors.text}}>Data & Privacy</h3>
            <div style={styles.settingsContent}>
              <div>
                <h4 style={{...styles.subsectionTitle, color: colors.text}}>Export Your Data</h4>
                <p style={{fontSize: '0.9rem', color: colors.textSecondary, marginBottom: '0.75rem'}}>
                  Download all your account data in JSON format.
                </p>
                <button onClick={handleExportData} style={styles.btnPrimary}>
                  Export Data
                </button>
              </div>
            </div>
          </div>
          )}

          {openSection === 'account' && (
          <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <h3 style={{...styles.sectionTitle, color: colors.text}}>Account</h3>
            <div style={styles.settingsContent}>
              <AccountSignIn
                mode="agent"
                email={profile && profile.email}
                colors={colors}
              />
              <div style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '1rem',
                marginBottom: '1rem',
                overflow: 'hidden',
              }}
              >
                <AgentPushSetting profile={profile} />
              </div>
              <AgentAccountDelete onDeleted={onLogout} />
            </div>
          </div>
          )}

          {openSection === 'support-access' && (
            <SupportAccessSettings colors={colors} />
          )}

      {!openSection && (
        <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, marginTop: '1.5rem'}}>
          <h3 style={{...styles.sectionTitle, color: colors.text}}>Appearance</h3>
          <div style={styles.settingsContent}>
            <p style={{fontSize: '0.9rem', color: colors.textSecondary, marginBottom: '0.75rem'}}>
              Choose your preferred color theme
            </p>
            <AppearanceChoice />
          </div>
        </div>
      )}

      {!openSection && (
        <div data-tour="more-rows" style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, padding: 0, marginTop: '1.5rem'}}>
          <button
            type="button"
            className="app-menu-row"
            onClick={() => setOpenSection('notifications')}
            style={menuRowStyle(colors)}
          >
            <span style={{flex: 1, textAlign: 'left'}}>Notifications</span>
            <span style={{color: colors.textSecondary}}>›</span>
          </button>
          <a
            className="app-menu-row"
            data-tour="legal"
            href={LEGAL_INDEX_PATH}
            onClick={followPolicyLink}
            style={{ ...menuRowStyle(colors), textDecoration: 'none', boxSizing: 'border-box', borderBottom: 'none' }}
          >
            <span style={{flex: 1, textAlign: 'left'}}>Legal</span>
            <span style={{ color: colors.textSecondary }}>›</span>
          </a>
        </div>
      )}

      {/* Logout sits in the same row style as the rest of More. */}
      {onLogout && !openSection && (
        <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, padding: 0, marginTop: '1.5rem'}}>
          <button
            type="button"
            className="app-menu-row native-tap"
            onClick={onLogout}
            style={{
              ...menuRowStyle(colors),
              color: '#dc2626',
              borderBottom: 'none',
              touchAction: 'manipulation',
            }}
          >
            <span style={{flex: 1, textAlign: 'left'}}>Logout</span>
          </button>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && !nativeApp && (
        <ModalLayer className="app-modal-layer" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div className="app-modal-card" style={{
            background: colors.surface,
            borderRadius: '1rem',
            padding: isMobile ? '1.5rem' : '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="app-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', background: colors.surface }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                color: colors.text
              }}>
                Change Your Plan
              </h2>
              <button
                type="button"
                className="app-modal-close"
                aria-label="Close"
                onClick={() => setShowChangePlanModal(false)}
                style={{ color: colors.text, background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <p style={{
              margin: '0 0 1.5rem 0',
              color: colors.textSecondary,
              fontSize: '0.95rem'
            }}>
              {hasSubscription
                ? 'Select a new plan. Your billing will be prorated automatically. '
                : showNewTrialLine
                  ? 'Select a plan. Checkout collects the payment method. New subscriptions include a 14-day free trial. '
                  : awaitingTrialCheck
                    ? 'Select a plan. Checkout collects the payment method. '
                    : 'Select a plan. Checkout charges your card today. '}
              Prices shown are monthly. We don't currently charge sales tax on subscriptions.
            </p>
            {seatLimitNote ? (
              <p style={{ margin: '0 0 1rem', color: colors.text }}>{seatLimitNote}</p>
            ) : null}

            {/* Solo Plan Option. A team switches to Solo from the Team page. */}
            {onTeamPlan ? (
              <p style={{ margin: '0 0 1rem', color: colors.text }}>
                Switching this team to Solo Agent is on the Team page, under Danger zone. This list only changes the number of team seats.
              </p>
            ) : null}
            {!onTeamPlan && (
            <div
              role="button"
              aria-disabled={planBlocked(1)}
              aria-pressed={selectedNewPlan?.type === 'solo'}
              onClick={() => {
                if (planBlocked(1)) return;
                setSelectedNewPlan({
                  type: 'solo',
                  price: seatMonthlyTotal(49, 1, currentAddon),
                  maxClients: 200,
                  priceId: STRIPE_PRICES.SOLO
                });
              }}
              style={{
                border: `2px solid ${selectedNewPlan?.type === 'solo' ? '#59c4c4' : colors.border}`,
                borderRadius: '0.75rem',
                padding: '1rem',
                marginBottom: '1rem',
                cursor: planBlocked(1) ? 'not-allowed' : 'pointer',
                opacity: planBlocked(1) ? 0.5 : 1,
                background: selectedNewPlan?.type === 'solo'
                  ? (colors.surfaceAlt || 'rgba(89, 196, 196, 0.1)')
                  : 'transparent',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedNewPlan?.type !== 'solo') {
                  e.currentTarget.style.background = colors.surfaceAlt || 'rgba(89, 196, 196, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedNewPlan?.type !== 'solo') {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: colors.text
                }}>
                  Solo Agent
                </span>
                <span style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#59c4c4'
                }}>
                  ${seatMonthlyTotal(49, 1, currentAddon)}/month
                </span>
              </div>
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                color: colors.textSecondary
              }}>
                Perfect for individual agents - Up to 200 homeowners
              </p>
            </div>
            )}

            {/* Team Plan Options */}
            <h3 style={{
              margin: '1.5rem 0 0.75rem 0',
              fontSize: '1rem',
              fontWeight: '600',
              color: colors.text,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              opacity: 0.8
            }}>
              Team Plans
            </h3>

            <div style={{
              display: 'grid',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              {TEAM_PRICING.map((teamOption) => {
                const blocked = planBlocked(teamOption.agents);
                const quote = addonQuote({ addon: currentAddon, seats: teamOption.agents, shared: true });
                const price = seatMonthlyTotal(teamOption.price, teamOption.agents, currentAddon);
                const addonNote = quote.addon === 'none'
                  ? ''
                  : `Includes ${quote.label}, +$${quote.monthlyPerAgent}/mo per seat`;
                return (
                <div
                  key={teamOption.agents}
                  role="button"
                  aria-disabled={blocked}
                  aria-pressed={selectedNewPlan?.type === 'team' && selectedNewPlan?.agents === teamOption.agents}
                  onClick={() => {
                    if (blocked) return;
                    setSelectedNewPlan({
                      type: 'team',
                      agents: teamOption.agents,
                      price,
                      maxClients: quote.pool,
                      priceId: teamOption.priceId
                    });
                  }}
                  style={{
                    border: `2px solid ${
                      selectedNewPlan?.type === 'team' && selectedNewPlan?.agents === teamOption.agents
                        ? '#59c4c4'
                        : colors.border
                    }`,
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    cursor: blocked ? 'not-allowed' : 'pointer',
                    opacity: blocked ? 0.5 : 1,
                    background: selectedNewPlan?.type === 'team' && selectedNewPlan?.agents === teamOption.agents
                      ? (colors.surfaceAlt || 'rgba(89, 196, 196, 0.1)')
                      : 'transparent',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (blocked) return;
                    if (!(selectedNewPlan?.type === 'team' && selectedNewPlan?.agents === teamOption.agents)) {
                      e.currentTarget.style.background = colors.surfaceAlt || 'rgba(89, 196, 196, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(selectedNewPlan?.type === 'team' && selectedNewPlan?.agents === teamOption.agents)) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      fontWeight: 'bold',
                      fontSize: '1.05rem',
                      color: colors.text
                    }}>
                      {checkoutPlanLabel('team', teamOption.agents)}
                    </span>
                    <span style={{
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      color: '#59c4c4'
                    }}>
                      ${price}/month
                    </span>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: '0.9rem',
                    color: colors.textSecondary
                  }}>
                    Up to {quote.pool.toLocaleString('en-US')} homeowners, shared by the team
                    {addonNote ? <span style={{ display: 'block', marginTop: '0.25rem' }}>{addonNote}</span> : null}
                  </p>
                </div>
              );})}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                margin: '0 0 0.75rem 0',
                fontSize: '1rem',
                fontWeight: '600',
                color: colors.text,
              }}>
                Add-ons
              </h3>
              {changePlanAddonLines().map((line) => (
                <p key={line} style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '0.95rem' }}>{line}</p>
              ))}
              <p style={{ margin: '0.5rem 0 0', color: colors.textSecondary, fontSize: '0.9rem' }}>
                {changePlanAddonNote()}
              </p>
            </div>

            {/* Modal Actions */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: `1px solid ${colors.border}`
            }}>
              <button
                onClick={() => {
                  setShowChangePlanModal(false);
                  setSelectedNewPlan(null);
                }}
                disabled={changingPlan}
                style={{
                  ...styles.btnSecondary,
                  opacity: changingPlan ? 0.6 : 1,
                  cursor: changingPlan ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={requestPlanChange}
                disabled={!selectedNewPlan || changingPlan}
                style={{
                  ...styles.btnPrimary,
                  opacity: (!selectedNewPlan || changingPlan) ? 0.6 : 1,
                  cursor: (!selectedNewPlan || changingPlan) ? 'not-allowed' : 'pointer'
                }}
              >
                {changingPlan ? 'Updating...' : 'Save plan'}
              </button>
            </div>
          </div>
        </ModalLayer>
      )}

      <ConfirmDialog
        open={Boolean(planConfirmMessage)}
        title="Change plan"
        message={planConfirmMessage}
        confirmLabel={selectedNewPlan && planNeedsSubscribeConsent(selectedNewPlan)
          ? subscribeButtonFor({
            planName: selectedNewPlan.type,
            agents: selectedNewPlan.agents || 1,
            addon: planChangeAddon(selectedNewPlan),
          })
          : (selectedNewPlan && planNeedsTrialConsent(selectedNewPlan)
            ? trialButtonFor({
              planName: selectedNewPlan.type,
              agents: selectedNewPlan.agents || 1,
              addon: planChangeAddon(selectedNewPlan),
              profile,
            })
            : 'Continue')}
        confirmDisabled={Boolean(
          (!hasSubscription && awaitingTrialCheck)
          || (selectedNewPlan && (planNeedsTrialConsent(selectedNewPlan) || planNeedsSubscribeConsent(selectedNewPlan)) && !trialPlanConsent)
        )}
        busy={changingPlan}
        onCancel={() => setPlanConfirmMessage('')}
        onConfirm={handleChangePlan}
      >
        {selectedNewPlan && (planNeedsTrialConsent(selectedNewPlan) || planNeedsSubscribeConsent(selectedNewPlan)) ? (
          <div>
            <p style={{ margin: '0 0 1rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{planConfirmMessage}</p>
            {planNeedsSubscribeConsent(selectedNewPlan) ? (
              <SubscribeConsentNotice
                planName={selectedNewPlan.type}
                agents={selectedNewPlan.agents || 1}
                addon={planChangeAddon(selectedNewPlan)}
                refundVariant={subscribeRefundVariant}
                checked={trialPlanConsent}
                onChange={setTrialPlanConsent}
                colors={colors}
              />
            ) : (
              <TrialConsentNotice
                planName={selectedNewPlan.type}
                agents={selectedNewPlan.agents || 1}
                addon={planChangeAddon(selectedNewPlan)}
                profile={profile}
                checked={trialPlanConsent}
                onChange={setTrialPlanConsent}
                colors={colors}
              />
            )}
          </div>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={Boolean(addonConfirmMessage)}
        title="Homeowner add-on"
        message={addonConfirmMessage}
        confirmLabel="Continue"
        busy={changingAddon}
        onCancel={() => { if (!changingAddon) setAddonConfirmMessage(''); }}
        onConfirm={handleChangeAddon}
      />
    </div>
  );
};

const styles = {
  pageTitle: {
    marginBottom: '1.5rem',
    fontSize: '1.8rem'
  },
  card: {
    background: 'white',
    padding: window.innerWidth < 768 ? '1.5rem' : '2rem',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '1rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem'
  },
  cardTitle: {
    marginBottom: '1rem',
    fontSize: '1.2rem'
  },
  sectionTitle: {
    marginBottom: '1.25rem',
    fontSize: window.innerWidth < 768 ? '1.1rem' : '1.25rem',
    fontWeight: '600',
    letterSpacing: '0.02em'
  },
  subsectionTitle: {
    marginBottom: '0.75rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    opacity: 0.8
  },
  settingsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1rem',
    alignSelf: 'flex-start'
  },
  btnSecondary: {
    background: '#666',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1rem',
    alignSelf: 'flex-start'
  },
  warning: {
    marginTop: '0.5rem',
    color: '#666',
    fontSize: '0.9rem'
  }
};

export default Settings;
